import { IoIosHelpCircleOutline } from "react-icons/io";
import { Link, useNavigate } from "react-router";

import { openLink } from "@tma.js/sdk-react";

import { Header } from "pages/ui/header/header";
import { Layout } from "pages/ui/page";
import { useAuthRedirectLinkMutation } from "shared/api/use-auth-redirect-link-mutation";
import { useConnectedAccountsQuery } from "shared/api/use-connected-accounts-query";
import Calendar from "shared/assets/svgs/calendar.svg?react";
import Gear from "shared/assets/svgs/gear.svg?react";
import Users from "shared/assets/svgs/users.svg?react";
import { GoogleButton } from "shared/ui/google-button";
import { ProviderTile } from "shared/ui/provider-tile";
import { plural } from "shared/utils";
import { cn } from "shared/utils/cn";

import { Item } from "./ui/item";

export const Settings = () => {
  const navigate = useNavigate();

  const accountsQuery = useConnectedAccountsQuery();
  const authRedirectLinkMutation = useAuthRedirectLinkMutation();

  const data = accountsQuery.data;

  const googleAccount = data?.connected_accounts.find(
    (account) => account.type === "google"
  );

  const icloudAccount = data?.connected_accounts.find(
    (account) => account.type === "icloud"
  );

  const hasAccounts = (data?.connected_accounts.length ?? 0) > 0;

  const handleGoogleReconnect = async () => {
    if (!googleAccount) return;
    try {
      const url = await authRedirectLinkMutation.mutateAsync({
        instructions: googleAccount?.instructions,
        label: googleAccount?.label,
        scopes: googleAccount?.scopes,
      });
      if (import.meta.env.DEV) {
        window.open(url, "_blank");
        return;
      }
      openLink(url, {
        tryInstantView: false,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSupport = () => {
    openLink("https://t.me/resonar_support_bot", {
      tryInstantView: true,
    });
  };

  return (
    <Layout withPattern back={false}>
      <Header />

      <div className="flex flex-col gap-2">
        <Item
          onClick={hasAccounts ? () => navigate("/accounts") : undefined}
          rightIcon={hasAccounts ? undefined : <></>}
          icon={<Users className="size-8" />}
          className={cn((!googleAccount || !icloudAccount) && "bg-oil")}
          title="Connected Accounts"
          description={
            hasAccounts
              ? `You have connected ${data?.connected_accounts.length} ${plural(data?.connected_accounts.length ?? 0, ["account", "accounts", "accounts"])}`
              : "You have no connected accounts"
          }
        >
          {!accountsQuery.isLoading &&
            (!hasAccounts || googleAccount?.is_outdated) && (
              <div className="flex flex-col gap-y-2.5">
                {!hasAccounts && (
                  <div className="border-dark-grey flex items-center justify-between rounded-xl border p-2 pl-3">
                    <span className="text-[15px] leading-5.5 font-medium tracking-[0.4px] text-white">
                      Connect your first account
                    </span>
                    <div className="flex gap-x-2">
                      <ProviderTile
                        provider="apple"
                        onClick={() => navigate("/connect/apple")}
                      />
                      <ProviderTile
                        provider="google"
                        onClick={() => navigate("/connect/google")}
                      />
                    </div>
                  </div>
                )}
                {googleAccount && googleAccount.is_outdated && (
                  <div className="flex flex-col gap-y-2.5">
                    <p className="text-pale-grey text-[15px] leading-4 select-none">
                      Connection of Google account{" "}
                      <span className="text-clear-blue">
                        {googleAccount.name}
                      </span>{" "}
                      is outdated.
                    </p>
                    <GoogleButton
                      isLoading={authRedirectLinkMutation.isPending}
                      onClick={(e) => {
                        handleGoogleReconnect();
                        e.stopPropagation();
                      }}
                    >
                      Reconnect with Google
                    </GoogleButton>
                  </div>
                )}

              </div>
            )}
        </Item>
        <Item
          as={Link}
          rightIconClassName="ml-auto"
          to="/preferences"
          icon={<Gear className="size-8" />}
          title="Preferences"
          description="Timezone, Language and Context"
        />
        <Item
          as={Link}
          rightIconClassName="ml-auto"
          to="/subscription"
          icon={<Calendar className="size-8" />}
          title="Subscription"
          description="You are using a paid version"
        />
        <Item
          rightIconClassName="ml-auto"
          onClick={handleSupport}
          icon={<IoIosHelpCircleOutline className="size-8" />}
          title="Support"
          description="Get help from Resonar team"
        />
      </div>
    </Layout>
  );
};
